'use client'

import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import Input from '@/components/ui/Input'
import Textarea from '@/components/ui/Textarea'
import Button from '@/components/ui/Button'
import { FaEnvelope, FaPhone, FaWhatsapp, FaCheck, FaTimes, FaBox } from 'react-icons/fa'
import { useProductSelection } from '@/contexts/ProductSelectionContext'

interface ContactTopic {
  id: string
  name: string
  slug: string
}

interface ContactFormData {
  topic_id: string
  name: string
  email: string
  phone: string
  company: string
  preferred_contact: 'email' | 'phone' | 'whatsapp'
  message: string
}

const preferredContactOptions = [
  { value: 'email', label: 'E-posta', icon: FaEnvelope, requiresEmail: true, requiresPhone: false },
  { value: 'phone', label: 'Telefon', icon: FaPhone, requiresEmail: false, requiresPhone: true },
  { value: 'whatsapp', label: 'WhatsApp', icon: FaWhatsapp, requiresEmail: false, requiresPhone: true },
]

interface ContactFormModalProps {
  isOpen: boolean
  onClose: () => void
}

export default function ContactFormModal({ isOpen, onClose }: ContactFormModalProps) {
  const { selectedProducts, clearSelection } = useProductSelection()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null)
  const [topics, setTopics] = useState<ContactTopic[]>([])
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    clearErrors,
  } = useForm<ContactFormData>({
    defaultValues: {
      preferred_contact: 'email',
    }
  })

  const preferredContact = watch('preferred_contact')

  // Seçilen iletişim tercihine göre hangi alanların gerekli olduğunu belirle
  const selectedOption = preferredContactOptions.find(opt => opt.value === preferredContact)
  const requiresEmail = selectedOption?.requiresEmail ?? true
  const requiresPhone = selectedOption?.requiresPhone ?? false

  // İletişim tercihi değiştiğinde hataları temizle
  useEffect(() => {
    clearErrors(['email', 'phone'])
  }, [preferredContact, clearErrors])

  // Modal açıldığında konuları yükle
  useEffect(() => {
    if (isOpen) {
      async function fetchTopics() {
        try {
          const response = await fetch('/api/contact/topics')
          if (response.ok) {
            const data = await response.json()
            setTopics(data)
          }
        } catch (error) {
          console.error('Konular yüklenirken hata:', error)
        }
      }
      fetchTopics()
    }
  }, [isOpen])

  // Modal kapandığında formu sıfırla
  useEffect(() => {
    if (!isOpen) {
      reset()
      setSubmitStatus(null)
    }
  }, [isOpen, reset])

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          product_ids: selectedProducts.map(p => p.id),
        }),
      })

      if (response.ok) {
        setSubmitStatus('success')
        reset()
        clearSelection()
        // 2 saniye sonra modal'ı kapat
        setTimeout(() => {
          onClose()
        }, 2000)
      } else {
        setSubmitStatus('error')
      }
    } catch (error) {
      console.error('Form gönderim hatası:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10">
            <h2 className="text-xl font-bold text-gray-900">Bize Yazın</h2>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Kapat"
            >
              <FaTimes className="text-gray-500 text-lg" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Seçilen Ürünler */}
            {selectedProducts.length > 0 && (
              <div className="mb-4 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <FaBox className="text-primary-600 text-sm" />
                  <span className="text-sm font-semibold text-primary-900">
                    {selectedProducts.length} ürün seçildi
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {selectedProducts.map(product => (
                    <span
                      key={product.id}
                      className="inline-flex items-center px-2 py-0.5 bg-white border border-primary-200 text-primary-700 rounded text-xs"
                    >
                      {product.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Konu Seçimi */}
              <div>
                <select
                  {...register('topic_id', { required: 'Lütfen bir konu seçiniz' })}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors bg-white"
                >
                  <option value="">Konu seçiniz... *</option>
                  {topics.map(topic => (
                    <option key={topic.id} value={topic.id}>
                      {topic.name}
                    </option>
                  ))}
                </select>
                {errors.topic_id && (
                  <p className="mt-1 text-xs text-red-600">{errors.topic_id.message}</p>
                )}
              </div>

              {/* Ad Soyad */}
              <Input
                placeholder="Ad Soyad *"
                {...register('name', { required: 'Ad soyad gereklidir' })}
                error={errors.name?.message}
              />

              {/* İletişim Tercihi */}
              <div>
                <div className="grid grid-cols-3 gap-2">
                  {preferredContactOptions.map(option => {
                    const Icon = option.icon
                    const isSelected = preferredContact === option.value
                    return (
                      <label
                        key={option.value}
                        className={`
                          flex items-center justify-center gap-1.5 p-2 rounded-lg border-2 cursor-pointer transition-all text-sm
                          ${isSelected 
                            ? 'border-primary-500 bg-primary-50 text-primary-700' 
                            : 'border-gray-200 hover:border-gray-300 text-gray-600 hover:bg-gray-50'
                          }
                        `}
                      >
                        <input
                          type="radio"
                          value={option.value}
                          {...register('preferred_contact')}
                          className="sr-only"
                        />
                        <Icon className="text-base" />
                        <span className="font-medium">{option.label}</span>
                      </label>
                    )
                  })}
                </div>
              </div>

              {/* Dinamik İletişim Alanı */}
              {requiresEmail && (
                <Input
                  type="email"
                  placeholder="E-posta adresiniz *"
                  {...register('email', {
                    required: requiresEmail ? 'E-posta adresiniz gereklidir' : false,
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Geçerli bir e-posta adresi giriniz',
                    },
                  })}
                  error={errors.email?.message}
                />
              )}
              
              {requiresPhone && (
                <Input
                  type="tel"
                  placeholder={preferredContact === 'whatsapp' ? 'WhatsApp numaranız *' : 'Telefon numaranız *'}
                  {...register('phone', {
                    required: requiresPhone ? 'Telefon numaranız gereklidir' : false,
                    pattern: {
                      value: /^[0-9\s\-\+\(\)]{10,}$/,
                      message: 'Geçerli bir telefon numarası giriniz',
                    },
                  })}
                  error={errors.phone?.message}
                />
              )}

              {/* Opsiyonel Alanlar */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {!requiresEmail && (
                  <Input
                    type="email"
                    placeholder="E-posta (opsiyonel)"
                    {...register('email', {
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Geçerli bir e-posta adresi giriniz',
                      },
                    })}
                    error={errors.email?.message}
                  />
                )}
                
                {!requiresPhone && (
                  <Input
                    type="tel"
                    placeholder="Telefon (opsiyonel)"
                    {...register('phone')}
                    error={errors.phone?.message}
                  />
                )}
                
                <Input
                  placeholder="Şirket / Kurum (opsiyonel)"
                  {...register('company')}
                  error={errors.company?.message}
                />
              </div>

              {/* Mesaj */}
              <Textarea
                rows={4}
                placeholder="Mesajınız *"
                {...register('message', { required: 'Mesaj gereklidir' })}
                error={errors.message?.message}
              />

              {/* Durum Mesajları */}
              {submitStatus === 'success' && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 flex items-start gap-2 text-sm">
                  <FaCheck className="text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Mesajınız başarıyla gönderildi!</p>
                    <p className="text-xs mt-0.5">En kısa sürede size dönüş yapacağız.</p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                  Mesaj gönderilirken bir hata oluştu. Lütfen tekrar deneyiniz.
                </div>
              )}

              {/* Butonlar */}
              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="flex-1"
                  size="sm"
                >
                  İptal
                </Button>
                <Button 
                  type="submit" 
                  disabled={isSubmitting} 
                  className="flex-1"
                  size="sm"
                >
                  {isSubmitting ? 'Gönderiliyor...' : 'Gönder'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
